import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect, useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';
import isEqual from 'underscore/modules/isEqual';
import { useTranslator } from 'tradera-lang/translate';
import {
    Breakpoint,
    BREAKPOINTS
} from 'tradera-components/breakpoint/breakpoint';
import ScrollToTopButton from 'components/shared/scroll-to-top-button';
import ApplicationErrorBoundary from 'tradera-components/application-error-boundary';
import Breadcrumbs from 'tradera-components/breadcrumbs/breadcrumbs';
import DiscoverPagination from '../components/search-results/pagination/discover-pagination';
import FilterCategoriesModal from '../components/filters/categories/filter-categories-modal';
import FilterModalFooter from '../components/filters/filter-modal-footer';
import InternalSearchForm from '../components/internal-search-form';
import IntroTextContainer from '../components/search-results/intro-text-container';
import ItemSettings from '../../../../../components/item-settings/item-settings';
import Loader from 'tradera-components/loader/loader';
import NoItemsError from 'tradera-components/errors/no-items-error';
import SaveSearchButton from 'tradera-apps/tradera/components/discover/components/search-results/save-search-button';
import SearchFilter from '../components/filters/search-filter';
import SearchFilterModal from '../components/filters/search-filter-modal';
import SearchResults from '../components/search-results/search-results';
import SearchResultsHeader from '../components/search-results/search-results-header';
import SideMenu from 'tradera-components/side-menu/side-menu';
import SiteSubheader from 'tradera-components/site-subheader/site-subheader';
import { useIsFeatureEnabled } from 'tradera-hooks/use-is-feature-enabled';
import QuickView from 'tradera-components/quick-view/quick-view-modal';
import CONSTANTS from '../constants/constants';
import ENDPOINTS from 'tradera-constants/endpoints';
import PAGE_TYPES from 'tradera-constants/page-types';
import WaitForConsentApiLoaded from 'tradera-components/wait-for-consent-api-loaded/wait-for-consent-api-loaded';
import AdsenseShopping from 'static/script/app/ads/adsense-shopping';
import Adsense from 'static/script/app/ads/adsense';
import { composeAdsenseChannel } from 'static/script/app/ui/adsense-utils';
import * as gtm from 'tradera-apps/tradera/components/discover/helpers/search-gtm';
import {
    formatQueryParams,
    stripCategoryIdFromQueryParameters,
    stripItmFromQueryParameters,
    stripPaginationFromQueryParameters,
    stripSavedQueryNameFromQueryParameters,
    toQueryParameters,
    toSearchString
} from 'tradera-utils/url';
import {
    createAttributeFilter,
    isTagNotInAttributeFilters,
    resolveHeaderCategory
} from './search-results-wrapper-logic';
import discoverActions from 'tradera-state/discover/actions';
import uiActions from 'tradera-state/ui/actions';
import '../components/search-results/search-results.scss';
import { LayoutA } from 'tradera-apps/tradera/layout/layout-a';
import { getTotalActiveFilterCount } from '../helpers/filter';

const SearchResultsWrapper = ({
    adSenseQuery,
    adSenseShoppingPublisherId,
    categories,
    match: {
        params: { categoryId }
    },
    className,
    displayAllowedBuyerRegionFilter,
    splitTestGroups,
    history,
    isLoggedIn,
    isSavedSearchEmailEnabled,
    isShopOwnedByCurrentMember,
    location,
    openSignIn,
    pageType,
    searchPath,
    setSingleFilterValue,
    showHeading,
    activeFilters = [],
    attributeFilters = [],
    endpoint,
    error,
    filterCategories = {},
    filterPrice,
    filters = {},
    hasResult,
    headerText,
    internalSearch,
    introText,
    mainText,
    pagination = {},
    queryParams,
    totalItems,
    noItemsFallback,
    loadStatus: { isLoading, isLoaded, setLoading, setLoaded }
}) => {
    const { categoryTree = {} } = filterCategories;
    const filtersLoading =
        (!filters || Object.keys(filters).length < 1) && isLoading;

    const attributeFiltersEnabled = useIsFeatureEnabled(
        'search-with-attribute-filters'
    );
    const isPrerenderReadyEnabled = useIsFeatureEnabled('prerender-ready');
    const isAdsenseForShoppingEnabled = useIsFeatureEnabled(
        'adsense-for-shopping'
    );

    const showSaveSearch =
        !queryParams.savedQueryName || queryParams.savedQueryName === '';

    const headerCategory = resolveHeaderCategory(
        filterCategories.activeCategory,
        categories,
        queryParams.categoryId,
        filterCategories.categoryTree,
        introText,
        headerText,
        pageType
    );

    const totalActiveFilters = getTotalActiveFilterCount(
        filterPrice.fromPrice,
        filterPrice.toPrice,
        attributeFilters,
        activeFilters
    );

    const { t } = useTranslator();

    const [modal, setModal] = useState({
        modalOpen: null,
        filterAccordionOpen: null
    });

    const dispatch = useDispatch();

    // Search
    useEffect(() => {
        if (isLoaded || isLoading) return;
        setLoading();
        const newQueryParams = stripItmFromQueryParameters(
            formatQueryParams(toQueryParameters(location.search))
        );
        delete newQueryParams.fe; // Remove workaround query parameter
        if (
            !hasResult ||
            searchPath !== endpoint ||
            !isEqual(newQueryParams, queryParams)
        ) {
            dispatch(
                discoverActions.fetchSearchResults(
                    searchPath,
                    newQueryParams,
                    pageType,
                    setLoaded
                )
            );
        } else {
            dispatch(
                discoverActions.afterReceiveSearchResults(pageType, setLoaded)
            );
        }
    }, [
        dispatch,
        endpoint,
        isLoaded,
        isLoading,
        hasResult,
        location,
        pageType,
        queryParams,
        searchPath,
        setLoading,
        setLoaded
    ]);

    useEffect(() => {
        if (!isLoaded || !isPrerenderReadyEnabled) {
            return;
        }
        window.prerenderReady = true;
    }, [dispatch, isLoaded, isPrerenderReadyEnabled]);

    // Scroll to top on new results
    useEffect(() => {
        if (isLoading) {
            const scrollToElement = document.getElementById(
                'scroll-to-top-anchor'
            );
            if (scrollToElement) {
                scrollToElement.scrollIntoView();
            } else {
                window.scrollTo(0, 0);
            }
        }
    }, [isLoading]);

    const pushToHistory = query => {
        let pathname;
        if (pageType === PAGE_TYPES.CATEGORY_RESULT_PAGE) {
            pathname = categoryId;
        } else if (pageType === PAGE_TYPES.CUSTOM_RESULT_PAGE) {
            pathname = location.pathname;
        } else {
            pathname = 'search';
        }
        history.push({
            pathname,
            search: toSearchString(query)
        });
    };

    const updateHistory = param => {
        const { savedQueryName, ...params } = queryParams;
        pushToHistory({
            ...formatQueryParams(
                stripPaginationFromQueryParameters(
                    stripSavedQueryNameFromQueryParameters(params)
                )
            ),
            ...param
        });
    };

    const checkForGtmTrackingOfAttributeFilter = (
        attributeFilters,
        tag,
        attributeFilter
    ) => {
        isTagNotInAttributeFilters(attributeFilters, attributeFilter, tag) &&
            gtm.trackFilter(attributeFilter, tag);
    };

    const handleClearAllFilters = () => {
        const { q, categoryId } = queryParams;
        pushToHistory({
            q,
            categoryId
        });
    };

    const handleCategoryChange = () => {};

    const handleTranslationStrategyClick = (
        q,
        originalQuery,
        overrideAutomaticTranslationPreference
    ) =>
        updateHistory({
            q,
            originalQuery,
            overrideAutomaticTranslationPreference
        });

    const handleSearchCategoryChange = category => {
        if (redirectEmptySearchToBrowse()) {
            gotoBrowseCategory(category);
        } else {
            updateHistory({ categoryId: category.id || null });
        }
    };

    const redirectEmptySearchToBrowse = () => {
        return !queryParams?.q && pageType === PAGE_TYPES.SEARCH_RESULT_PAGE;
    };

    const gotoBrowseCategory = category => {
        const { ...params } = queryParams;
        const query = {
            ...stripPaginationFromQueryParameters(
                stripCategoryIdFromQueryParameters(params)
            )
        };
        history.push({
            pathname: category.url,
            search: toSearchString(query)
        });
    };

    const handleFilterChange = (filterType, value) => {
        gtm.trackFilter(filterType, value);
        updateHistory({ [filterType]: value });
        setSingleFilterValue(filterType, value);
    };

    const handlePriceFilterChange = priceFilters => {
        updateHistory({
            ['fromPrice']: priceFilters ? priceFilters.fromPrice : null,
            ['toPrice']: priceFilters ? priceFilters.toPrice : null
        });
    };

    const handleAttributeFilterChange = (filter, values) => {
        checkForGtmTrackingOfAttributeFilter(attributeFilters, values, filter);
        const attributeFilterParameters = createAttributeFilter(
            attributeFilters,
            filter,
            values
        );
        updateHistory(attributeFilterParameters);
    };

    const handleInternalSearch = query => {
        const q = query.trim();
        if (q) {
            pushToHistory({ q });
        } else {
            pushToHistory(null);
        }
    };

    const handleModalClick = (value, filterAccordionOpen) => {
        setModal({
            modalOpen: value,
            filterAccordionOpen: filterAccordionOpen
        });
    };

    const isAdsenseEnabled = () => {
        if (!isAdsenseForShoppingEnabled) {
            return false;
        }

        if (
            splitTestGroups['adsense_for_shopping_loggedin'] ===
                'enabled_loggedin' &&
            'enabled_loggedin' &&
            isLoggedIn
        ) {
            return true;
        } else if (
            splitTestGroups['adsense_for_shopping_loggedin'] === 'enabled_all'
        ) {
            return true;
        }
        return false;
    };

    if (!hasResult) return <Loader usePortal={false} />;
    if (error) return <NoItemsError />;

    const subContent = (
        <div className="search-filter-wrapper d-none d-lg-block">
            {internalSearch && internalSearch.showSearchBar && (
                <InternalSearchForm
                    internalSearch={internalSearch}
                    handleInternalSearch={handleInternalSearch}
                />
            )}
            <SearchFilter
                allowedBuyerRegionFilterEnabled={
                    displayAllowedBuyerRegionFilter
                }
                attributeFilters={attributeFilters}
                attributeFiltersEnabled={attributeFiltersEnabled}
                filterCategories={filterCategories}
                filterPrice={filterPrice}
                filters={filters}
                handleAttributeFilterChange={handleAttributeFilterChange}
                handleCategoryChange={handleCategoryChange}
                handleFilterChange={handleFilterChange}
                handlePriceFilterChange={handlePriceFilterChange}
                handleSearchCategoryChange={handleSearchCategoryChange}
                pageType={pageType}
                queryParams={queryParams}
            />
        </div>
    );

    const mainContent = (
        <div className="search-results-wrapper">
            <SearchResultsHeader
                handleAttributeFilterChange={handleAttributeFilterChange}
                handleTranslationStrategyClick={handleTranslationStrategyClick}
                handleCategoryChange={handleCategoryChange}
                handleClearAllFilters={handleClearAllFilters}
                handleFilterChange={handleFilterChange}
                handleInternalSearch={handleInternalSearch}
                handleModalClick={handleModalClick} // Replace with portal in header?
                handlePriceFilterChange={handlePriceFilterChange}
                handleSearchCategoryChange={handleSearchCategoryChange}
                headerCategory={headerCategory}
                pageType={pageType}
            />
            <SearchResults
                handleItemAttributesForFiltering={handleAttributeFilterChange}
                isShopOwnedByCurrentMember={isShopOwnedByCurrentMember}
                pageType={pageType}
                noItemsFallback={
                    totalActiveFilters === 0 &&
                    !filterCategories.activeCategory &&
                    noItemsFallback
                }
                isLoaded={isLoaded}
            />
            <DiscoverPagination
                pagination={pagination}
                queryParams={queryParams}
            />
            {totalItems > 0 && <ScrollToTopButton />}
            {adSenseQuery && adSenseShoppingPublisherId && isAdsenseEnabled() && (
                <ApplicationErrorBoundary>
                    <WaitForConsentApiLoaded
                        onLoadRender={() => (
                            <AdsenseShopping
                                publisherId={adSenseShoppingPublisherId}
                                searchQuery={adSenseQuery}
                                className="mt-4"
                            />
                        )}
                    />
                </ApplicationErrorBoundary>
            )}

            {adSenseQuery &&
                isAdsenseEnabled() &&
                typeof pagination !== 'undefined' && (
                    <ApplicationErrorBoundary>
                        <WaitForConsentApiLoaded
                            onLoadRender={() => (
                                <Adsense
                                    searchQuery={adSenseQuery}
                                    channel={composeAdsenseChannel(pageType, [
                                        categoryTree.name
                                    ])}
                                    pageIndex={pagination.pageIndex}
                                    className="mt-5"
                                />
                            )}
                        />
                    </ApplicationErrorBoundary>
                )}
            {mainText && <IntroTextContainer html={mainText} />}
        </div>
    );

    return (
        <>
            {showHeading && (
                <Breakpoint downTo={BREAKPOINTS.LG}>
                    <SiteSubheader className="mb-0 mb-md-2 mb-lg-3 px-2">
                        <Breadcrumbs
                            categoryTree={filterCategories?.categoryTree}
                            handleClick={
                                pageType === PAGE_TYPES.SEARCH_RESULT_PAGE
                                    ? handleSearchCategoryChange
                                    : null
                            }
                            inSPA={true}
                        />
                        {showSaveSearch && (
                            <div className="ml-auto d-flex">
                                <p className="align-self-center mb-0 mr-3 text-gray-600">
                                    {t('searchResults_savedSearchBanner')}:
                                </p>
                                <SaveSearchButton
                                    categoryId={headerCategory.categoryId}
                                    isLoggedIn={isLoggedIn}
                                    isSavedSearchEmailEnabled={
                                        isSavedSearchEmailEnabled
                                    }
                                    openSignIn={openSignIn}
                                    queryParams={queryParams}
                                    searchName={
                                        queryParams.q ||
                                        headerCategory?.headerText
                                    }
                                />
                            </div>
                        )}
                    </SiteSubheader>
                </Breakpoint>
            )}
            <Breakpoint upTo={BREAKPOINTS.MD}>
                <SideMenu
                    show={modal.modalOpen === CONSTANTS.MODALS.CATEGORY_MODAL}
                    onDismiss={() => handleModalClick('')}>
                    <div className="search-modal bg-white d-flex flex-column">
                        <FilterCategoriesModal
                            filterCategories={filterCategories}
                            handleCategoryChange={handleCategoryChange}
                            handleSearchCategoryChange={
                                handleSearchCategoryChange
                            }
                            pageType={pageType}
                            isUpdating={isLoading}
                            queryParams={queryParams}
                        />
                        <FilterModalFooter
                            handleModalClick={handleModalClick}
                        />
                    </div>
                </SideMenu>
                <SideMenu
                    show={modal.modalOpen === CONSTANTS.MODALS.FILTER_MODAL}
                    onDismiss={() => handleModalClick('')}
                    fromLeft={false}>
                    <div className="search-modal search-modal--filters bg-white d-flex flex-column">
                        <SearchFilterModal
                            attributeFiltersEnabled={attributeFiltersEnabled}
                            filterAccordionOpen={modal.filterAccordionOpen}
                            handleAttributeFilterChange={
                                handleAttributeFilterChange
                            }
                            handleClearAllFilters={handleClearAllFilters}
                            handleFilterChange={handleFilterChange}
                            handlePriceFilterChange={handlePriceFilterChange}
                            isLoading={isLoading}
                            pageType={pageType}
                            queryParams={queryParams}
                            filters={filters}
                            filterPrice={filterPrice}
                            attributeFilters={attributeFilters}
                            totalItems={totalItems}
                            activeFilters={activeFilters}
                        />
                        <FilterModalFooter
                            handleModalClick={handleModalClick}
                        />
                    </div>
                </SideMenu>
            </Breakpoint>
            <QuickView />
            {isShopOwnedByCurrentMember && <ItemSettings />}
            <LayoutA
                aside={subContent}
                mainContent={mainContent}
                classes={`search-results-page ${className || ''}`}>
                {!modal.modalOpen && (isLoading || filtersLoading) && (
                    <Loader usePortal={false} />
                )}
            </LayoutA>
        </>
    );
};

SearchResultsWrapper.propTypes = {
    adSenseQuery: PropTypes.string,
    adSenseShoppingPublisherId: PropTypes.string,
    activeFilters: PropTypes.array,
    attributeFilters: PropTypes.array,
    className: PropTypes.string,
    filterCategories: PropTypes.object,
    hasResult: PropTypes.bool,
    history: PropTypes.object,
    isLoggedIn: PropTypes.bool,
    location: PropTypes.object,
    openSignIn: PropTypes.func.isRequired,
    pageType: PropTypes.string.isRequired,
    searchPath: PropTypes.string.isRequired,
    searchResults: PropTypes.object,
    setSingleFilterValue: PropTypes.func,
    showHeading: PropTypes.bool,
    loadStatus: PropTypes.shape({
        isLoading: PropTypes.bool,
        isLoaded: PropTypes.bool,
        setLoading: PropTypes.func,
        setLoaded: PropTypes.func
    }),
    noItemsFallback: PropTypes.node
};

SearchResultsWrapper.defaultProps = {
    className: '',
    searchPath: ENDPOINTS.SEARCH,
    showHeading: true
};

const mapStateToProps = state => {
    return {
        adSenseQuery: state.ads.adSenseQuery,
        adSenseShoppingPublisherId: state.ads.adSenseShoppingPublisherId,
        categories: state.page.allCategories,
        splitTestGroups: state.environment.splitTestGroups,
        isLoggedIn: state.member.isLoggedIn,
        searchResults: state.discover,
        activeFilters: state.discover.activeFilters,
        attributeFilters: state.discover.attributeFilters,
        endpoint: state.discover.endpoint,
        error: state.discover.error,
        filterCategories: state.discover.filterCategories,
        filterPrice: state.discover.filterPrice,
        filters: state.discover.filters,
        hasResult: state.discover.hasResult,
        headerText: state.discover.headerText,
        internalSearch: state.discover.internalSearch,
        introText: state.discover.introText,
        isShopOwnedByCurrentMember: state.discover.isShopOwnedByCurrentMember,
        isSavedSearchEmailEnabled: state.discover.isSavedSearchEmailEnabled,
        mainText: state.discover.mainText,
        pagination: state.discover.pagination,
        queryParams: state.discover.queryParams,
        totalItems: state.discover.totalItems,
        displayAllowedBuyerRegionFilter:
            state.discover.displayAllowedBuyerRegionFilter
    };
};

const mapDispatchToProps = dispatch => {
    return {
        openSignIn: redirectUrl => dispatch(uiActions.signIn({ redirectUrl })),
        setSingleFilterValue: (filterType, value) =>
            dispatch(discoverActions.setSingleFilterValue(filterType, value))
    };
};

export default withRouter(
    connect(mapStateToProps, mapDispatchToProps)(SearchResultsWrapper)
);
